import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Helmet } from 'react-helmet';
import Loader from '../../../components/Loader';
import axios from 'axios';
import params from '../../../config/Params';
import { getItemFromLocalStorage } from '../../../utils/storageUtils';
import { toast } from 'react-toastify';

const BankAccountInfo = forwardRef(({ formData, setFormData }, ref) => {
    const [loading, setLoading] = useState(true);
    const [baseData, setBaseData] = useState([]);
    const [selectedMethodId, setSelectedMethodId] = useState(formData.bankType ? formData.bankType.id : null);

    const [formFields, setFormFields] = useState({
        accountNumber: formData.accountNumber || '',
        confirmAccountNumber: formData.confirmAccountNumber || '',
        ifsCode: formData.ifsCode || '',
        panNumber: formData.panNumber || '',
        bankName: formData.bankName || '',
    });

    const [formErrors, setFormErrors] = useState({});

    useEffect(() => {
        const fetchBaseData = async () => {
            const hostData = getItemFromLocalStorage('hostProfile');
            const requestFormData = new FormData();
            requestFormData.append('token', hostData.host_access_token);

            try {
                const response = await axios.post(
                    `${params.baseURL}${params.endpoints.payoutBaseData}`,
                    requestFormData,
                    {
                        headers: { 'Content-Type': 'multipart/form-data' },
                    }
                );

                if (response.data.status === 'found') {
                    setBaseData(response.data.account_type);
                } else {
                    toast.error('An error occurred while fetching data.');
                }
            } catch (err) {
                toast.error(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchBaseData();
    }, []);

    const fetchBankDetails = async (code) => {
        const hostData = getItemFromLocalStorage('hostProfile');
        const requestFormData = new FormData();
        requestFormData.append('token', hostData.host_access_token);
        requestFormData.append('ifsc', code);

        try {
            const response = await axios.post(
                `${params.baseURL}${params.endpoints.payoutIfsCode}`,
                requestFormData,
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );

            if (response.data.status === 'ok') {
                const bankName = response.data.ifsc_info.BANK;
                const streetAddress = response.data.ifsc_info.ADDRESS;
                const city = response.data.ifsc_info.ADDRESS;
                const state = response.data.ifsc_info.ADDRESS;
                setFormFields((prev) => ({
                    ...prev,
                    bankName,
                }));
                setFormData((prev) => ({
                    ...prev,
                    bankName,
                }));
                setFormData((prev) => ({
                    ...prev,
                    streetAddress,
                }));
                setFormData((prev) => ({
                    ...prev,
                    city,
                }));
                setFormData((prev) => ({
                    ...prev,
                    state,
                }));
            } else {
                toast.error('Bank details not found for this IFSC code.');
            }
        } catch (err) {
            toast.error('Failed to fetch bank details.');
        }
    };

    const handleMethodChange = (id) => {
        setSelectedMethodId(id);
        const selectedMethod = baseData.find(method => method.id === id);
        setFormData(prev => ({ ...prev, bankType: selectedMethod }));
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormFields((prev) => ({
            ...prev,
            [name]: value,
        }));
        setFormErrors((prev) => ({
            ...prev,
            [name]: '',
        }));
        setFormData(prev => ({
            ...prev,
            [name]: value,
        }));

        if (name === 'ifsCode' && /^[A-Z]{4}0[A-Z0-9]{6}$/.test(value)) {
            fetchBankDetails(value);
        }
    };

    const validate = () => {
        const errors = {};
        const { accountNumber, confirmAccountNumber, ifsCode, panNumber } = formFields;

        if (!accountNumber) errors.accountNumber = 'Account number is required.';
        if (!confirmAccountNumber) errors.confirmAccountNumber = 'Confirm account number is required.';
        if (accountNumber && confirmAccountNumber && accountNumber !== confirmAccountNumber)
            errors.confirmAccountNumber = 'Account numbers do not match.';
        if (!ifsCode) errors.ifsCode = 'IFSC code is required.';
        else if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(ifsCode))
            errors.ifsCode = 'Invalid IFSC code format.';
        if (!panNumber) errors.panNumber = 'PAN number is required.';
        else if (!/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(panNumber))
            errors.panNumber = 'Invalid PAN number format.';

        setFormErrors(errors);
        return errors;
    };

    useImperativeHandle(ref, () => ({
        validate
    }));

    return (
        <div>
            <Helmet>
                <title>Add Bank Account Info</title>
            </Helmet>

            <div className="payout-setup">
                <div className='ws-cl-header'>
                    <div>
                        <h4 className='ws-title'>Add bank account info</h4>
                    </div>
                </div>

                {loading ? (
                    <Loader />
                ) : baseData && baseData.length > 0 ? (
                    <>
                        {baseData.map((method) => (
                            <div className='po-form-box' key={method.id}>
                                <div className='form-flex form-icon-box'>
                                    <div className='form-icon'>
                                        <img
                                            src='/assets/img/icons/pointer_icon.svg'
                                            className='form-icon img-fluid'
                                            alt=''
                                        />
                                    </div>
                                    <div className='form-txt drawer-policy-box mb-0'>
                                        <label className='policy-radio-input'>
                                            <div className='policy-radio-txt-box'>
                                                <span><div>{method.title}</div></span>
                                            </div>
                                            <div className="circle"></div>
                                            <input
                                                type="radio"
                                                name="account_type"
                                                value={method.id}
                                                checked={selectedMethodId === method.id}
                                                onChange={() => handleMethodChange(method.id)}
                                            />
                                        </label>
                                    </div>
                                </div>
                            </div>
                        ))}

                        <div className="po-form-box">
                            <div className='form-flex form-icon-box'>
                                <div className='form-icon'>
                                    <img
                                        src='/assets/img/icons/pointer_icon.svg'
                                        className='form-icon img-fluid'
                                        alt=''
                                    />
                                </div>
                                <div className='form-txt'>
                                    <div className='form-floating'>
                                        <input
                                            className={`form-control ${formErrors.accountNumber ? 'is-invalid' : ''}`}
                                            type="text"
                                            id="accountNumber"
                                            name="accountNumber"
                                            value={formFields.accountNumber}
                                            onChange={handleChange}
                                        />
                                        <label htmlFor="accountNumber">Account Number</label>
                                        {formErrors.accountNumber && <span className="error-icon">
                                            <i className="bi bi-exclamation-circle-fill text-danger"></i>
                                            <span className="tooltip-text">This field is required</span>
                                        </span>}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="po-form-box">
                            <div className='form-flex form-icon-box'>
                                <div className='form-icon'>
                                    <img
                                        src='/assets/img/icons/pointer_icon.svg'
                                        className='form-icon img-fluid'
                                        alt=''
                                    />
                                </div>
                                <div className='form-txt'>
                                    <div className='form-floating'>
                                        <input
                                            className={`form-control ${formErrors.confirmAccountNumber ? 'is-invalid' : ''}`}
                                            type="text"
                                            id="confirmAccountNumber"
                                            name="confirmAccountNumber"
                                            value={formFields.confirmAccountNumber}
                                            onChange={handleChange}
                                        />
                                        <label htmlFor="confirmAccountNumber">Confirm Account Number</label>
                                        {formErrors.confirmAccountNumber && <span className="error-icon">
                                            <i className="bi bi-exclamation-circle-fill text-danger"></i>
                                            <span className="tooltip-text">This field is required</span>
                                        </span>}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="po-form-box">
                            <div className='form-flex form-icon-box'>
                                <div className='form-icon'>
                                    <img
                                        src='/assets/img/icons/pointer_icon.svg'
                                        className='form-icon img-fluid'
                                        alt=''
                                    />
                                </div>
                                <div className='form-txt'>
                                    <div className='form-floating'>
                                        <input
                                            className={`form-control ${formErrors.ifsCode ? 'is-invalid' : ''}`}
                                            type="text"
                                            id="ifsCode"
                                            name="ifsCode"
                                            value={formFields.ifsCode}
                                            onChange={handleChange}
                                        />
                                        <label htmlFor="ifsCode">IFS Code</label>
                                        {formErrors.ifsCode && <span className="error-icon">
                                            <i className="bi bi-exclamation-circle-fill text-danger"></i>
                                            <span className="tooltip-text">This field is required</span>
                                        </span>}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="po-form-box">
                            <div className='form-flex form-icon-box'>
                                <div className='form-icon'>
                                    <img
                                        src='/assets/img/icons/pointer_icon.svg'
                                        className='form-icon img-fluid'
                                        alt=''
                                    />
                                </div>
                                <div className='form-txt'>
                                    <div className='form-floating'>
                                        <input
                                            className={`form-control ${formErrors.panNumber ? 'is-invalid' : ''}`}
                                            type="text"
                                            id="panNumber"
                                            name="panNumber"
                                            value={formFields.panNumber}
                                            onChange={handleChange}
                                        />
                                        <label htmlFor="panNumber">Permanent Account Number</label>
                                        {formErrors.panNumber && <span className="error-icon">
                                            <i className="bi bi-exclamation-circle-fill text-danger"></i>
                                            <span className="tooltip-text">This field is required</span>
                                        </span>}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="po-form-box">
                            <div className='form-flex form-icon-box'>
                                <div className='form-icon'>
                                    <img
                                        src='/assets/img/icons/pointer_icon.svg'
                                        className='form-icon img-fluid'
                                        alt=''
                                    />
                                </div>
                                <div className='form-txt'>
                                    <div className='form-floating'>
                                        <input
                                            type="text"
                                            className="form-control"
                                            id="bankName"
                                            name="bankName"
                                            value={formFields.bankName}
                                            onChange={handleChange}
                                        />
                                        <label htmlFor="bankName">Bank Name</label>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                ) : (
                    <p>No bank account options are available at the moment.</p>
                )}
            </div>
        </div>
    );
});

export default BankAccountInfo;